import { OrderableItems } from "@/components";
import { EntityStatus, ValueType } from "@/enums";
import { optionType, refType } from "@/propTypes";
import { faBars, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form, Input, Modal } from "antd";
import { arrayOf, object } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { OptionEditLocationValue } from "./OptionEditLocationValue";
import { OptionEditStatusValue } from "./OptionEditStatusValue";
import { OptionEditUserValue } from "./OptionEditUserValue";
import { OptionEditValue } from "./OptionEditValue";
import { FormFieldContainer } from "./styled";

export const OptionsEditFormField = ({
  availableDependantValues,
  form,
  formProperty,
}) => {
  const intl = useIntl();

  const [orderModalState, setOrderModalState] = useState({
    show: false,
    options: [],
  });

  const statusFormTargetValueType = {
    Intake: ValueType.FormPropertyProjectIntakeStatusValue,
  };

  const locationFormTargetValueType = {
    request: ValueType.FormPropertyLocationValue,
    labRequest: ValueType.FormPropertyLocationValue,
  };

  useEffect(() => {
    const dependsOnId = form.current.getFieldValue([
      "property",
      "dependsOnPropertyId",
    ]);

    //checking that availableDependantValues has some things, because when the form is first loaded
    //we dont have the availableDependantValues and we dont want to clear them out
    //this means that if they select a dependent property that doesnt have values, but the last one did
    //it will not correctly remove the old values, but I think that will just have to be.
    if ((availableDependantValues?.length || !dependsOnId) && form.current) {
      //if this has changed, make sure none of the options are using a value not in this list.
      const values = form.current.getFieldValue("Values");

      const availIds = availableDependantValues?.length
        ? availableDependantValues.map(({ value }) => value)
        : [];

      if (values) {
        const updatedValues = values.map((val) => {
          if (val.DependsOnPropertyValues) {
            val.DependsOnPropertyValues = val.DependsOnPropertyValues.filter(
              (v) => availIds.indexOf(v) >= 0
            );
          }
          return val;
        });
        form.current.setFieldsValue({ values: updatedValues });
      }
    }
  }, [availableDependantValues, form]);

  const showModal = () => {
    const values = form.current.getFieldValue("values");
    setOrderModalState({
      show: true,
      options: values,
    });
  };

  const saveModal = () => {
    form.current.setFieldsValue({ Values: [...orderModalState.options] });
    hideModal();
  };

  const hideModal = () => {
    setOrderModalState({ show: false, options: [] });
  };

  const getBaseValues = () => ({
    status: EntityStatus.Active,
    id: `temp-${new Date().valueOf()}`,
  });

  if (!form.current || !formProperty) return "";

  return (
    <>
      <FormFieldContainer>
        <Form.Item
          name={["property", "label", "fallback"]}
          label={intl.formatMessage({ id: "newFormField.fieldLabel" })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: "newFormField.fieldLabelRequired",
              }),
            },
          ]}
        >
          <Input className="viper-form-element-max-width" />
        </Form.Item>
        <Form.Item
          name={["property", "placeholder", "fallback"]}
          label={intl.formatMessage({ id: "newFormField.placeholder" })}
        >
          <Input className="viper-form-element-max-width" />
        </Form.Item>
        <Form.Item
          name={["property", "requiredValidationText", "fallback"]}
          label={intl.formatMessage({ id: "newFormField.validationErrorText" })}
        >
          <Input className="viper-form-element-max-width" />
        </Form.Item>
        <div className="ant-form-item-label">
          <label>{intl.formatMessage({ id: "newFormField.options" })}</label>
          <Button
            data-test-id="options-edit-form-field-sort"
            type="default"
            onClick={() => showModal()}
            icon={<FontAwesomeIcon icon={faBars} />}
            key="begin-sort"
            style={{ marginLeft: "10px" }}
          >
            {intl.formatMessage({ id: "general.reorder" })}
          </Button>
        </div>

        <Form.List name="values">
          {(fields, { add, remove }) => {
            const sharedOptionEditProps = {
              remove: remove,
              availableDependantValues: availableDependantValues,
            };

            return (
              <div className="even-odd-colors-container">
                {fields.map((field) => {
                  sharedOptionEditProps.field = field;

                  const valueType = form.current.getFieldValue([
                    "values",
                    field.name,
                    "valueType",
                  ]);
                  const id = form.current.getFieldValue([
                    "values",
                    field.name,
                    "id",
                  ]);

                  const isNew = !id || id.startsWith("temp-");

                  if (
                    Object.values(statusFormTargetValueType).includes(valueType)
                  ) {
                    return (
                      <OptionEditStatusValue
                        {...sharedOptionEditProps}
                        key={field.fieldKey}
                        labelDisabled={
                          valueType === ValueType.FormPropertyUserValue
                        }
                        isNew={isNew}
                        formRef={form}
                      />
                    );
                  }

                  if (valueType === ValueType.FormPropertyUserValue && isNew) {
                    //only allow people to edit user values when they are new
                    return (
                      <OptionEditUserValue
                        {...sharedOptionEditProps}
                        key={field.fieldKey}
                        form={form}
                      />
                    );
                  }

                  if (valueType === ValueType.FormPropertyLocationValue) {
                    return (
                      <OptionEditLocationValue
                        {...sharedOptionEditProps}
                        key={field.fieldKey}
                      />
                    );
                  }

                  return (
                    <OptionEditValue
                      {...sharedOptionEditProps}
                      key={field.fieldKey}
                      labelDisabled={
                        valueType === ValueType.FormPropertyUserValue
                      }
                      isNew={isNew}
                      formProperty={formProperty}
                    />
                  );
                })}

                <Form.Item>
                  <Button
                    data-test-id="options-edit-form-field-add-option"
                    type="dashed"
                    onClick={() => {
                      const value = getBaseValues();

                      if (
                        formProperty.property.name === "status" &&
                        Object.keys(statusFormTargetValueType).includes(
                          formProperty.property.target
                        )
                      ) {
                        const valueType =
                          statusFormTargetValueType[
                            formProperty.property.target
                          ];
                        value.valueType = valueType;
                      } else {
                        value.ValueType = ValueType.FormPropertyValue;
                      }

                      add(value);
                    }}
                    block
                    icon={<FontAwesomeIcon icon={faPlusCircle} />}
                  >
                    {intl.formatMessage({ id: "newFormField.addOption" })}
                  </Button>
                  {formProperty.property.name === "location" &&
                    Object.keys(locationFormTargetValueType).includes(
                      formProperty.property.target
                    ) && (
                      <Button
                        data-test-id="options-edit-form-field-add-location"
                        type="dashed"
                        onClick={() => {
                          add({
                            ...getBaseValues(),
                            valueType:
                              locationFormTargetValueType[
                                formProperty.property.target
                              ],
                          });
                        }}
                        block
                        icon={<FontAwesomeIcon icon={faPlusCircle} />}
                      >
                        {intl.formatMessage({ id: "newFormField.addLocation" })}
                      </Button>
                    )}
                  {!(
                    formProperty.property.name === "status" &&
                    Object.keys(statusFormTargetValueType).includes(
                      formProperty.property.target
                    )
                  ) && (
                    <Button
                      data-test-id="options-edit-form-field-add-user"
                      type="dashed"
                      onClick={() => {
                        add({
                          ...getBaseValues(),
                          ValueType: ValueType.FormPropertyUserValue,
                        });
                      }}
                      block
                      icon={<FontAwesomeIcon icon={faPlusCircle} />}
                    >
                      {intl.formatMessage({ id: "newFormField.addUser" })}
                    </Button>
                  )}
                </Form.Item>
              </div>
            );
          }}
        </Form.List>
      </FormFieldContainer>

      <Modal
        title={intl.formatMessage({
          id: "newFormField.sortOptions",
          defaultMessage: "Sort Options",
        })}
        onOk={saveModal}
        onCancel={hideModal}
        open={orderModalState.show}
        maskClosable={false}
      >
        <OrderableItems
          items={orderModalState.options}
          idKey={({ id }) => id}
          nameKey={({ label: { fallback } }) => fallback}
          setItems={(updatedItems) => {
            setOrderModalState((value) => ({
              ...value,
              options: updatedItems.map((item, index) => ({
                ...item,
                Order: index,
              })),
            }));
          }}
        />
      </Modal>
    </>
  );
};

OptionsEditFormField.propTypes = {
  form: refType,
  formProperty: object,
  availableDependantValues: arrayOf(optionType).isRequired,
};
